/**
 * OPERATIONAL LINEAGE NODE INSPECTOR
 *
 * Compact read-only inspection of a selected lineage node.
 * Uses operationalVisualLanguage tokens exclusively.
 */

import React from 'react';
import type { LineageNode } from '../operationalLineageTypes.js';
import { NEUTRAL, TYPOGRAPHY, SPACING } from '../../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface Props {
  node: LineageNode | null;
}

const labelStyle: React.CSSProperties = {
  fontSize: TYPOGRAPHY.labelSmall.size,
  color: NEUTRAL.text300,
  fontWeight: TYPOGRAPHY.labelSmall.weight,
  fontFamily: 'system-ui, sans-serif',
  minWidth: 48,
  display: 'inline-block',
};

const valueStyle: React.CSSProperties = {
  fontSize: TYPOGRAPHY.micro.size,
  color: NEUTRAL.text500,
  fontFamily: TYPOGRAPHY.mono.family,
};

function MetadataRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', gap: SPACING.sp2, marginBottom: SPACING.sp1, alignItems: 'baseline' }}>
      <span style={labelStyle}>{label}</span>
      <span style={valueStyle}>{value}</span>
    </div>
  );
}

export const OperationalLineageNodeInspector: React.FC<Props> = ({ node }) => {
  if (!node) {
    return (
      <div data-testid="lineage-inspector-empty" style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text300 }}>
        Select a node to inspect.
      </div>
    );
  }

  const metaEntries = Object.entries(node.metadata);

  return (
    <div data-testid="lineage-node-inspector">
      <div
        style={{
          fontSize: TYPOGRAPHY.label.size,
          fontWeight: TYPOGRAPHY.label.weight,
          color: NEUTRAL.text600,
          marginBottom: SPACING.sp2,
          wordBreak: 'break-word',
        }}
      >
        {node.label}
      </div>
      <MetadataRow label="type" value={node.type} />
      <MetadataRow label="id" value={node.id} />
      {node.hash && <MetadataRow label="hash" value={node.hash.length > 28 ? node.hash.slice(0, 28) + '…' : node.hash} />}
      <MetadataRow label="created" value={node.createdAt} />

      {metaEntries.length > 0 && (
        <div style={{ marginTop: SPACING.sp3 }}>
          <div style={{ fontSize: TYPOGRAPHY.labelSmall.size, color: NEUTRAL.text300, marginBottom: SPACING.sp1 }}>Metadata</div>
          {metaEntries.map(([key, val]) => (
            <div
              key={key}
              style={{
                display: 'flex',
                gap: SPACING.sp2,
                marginBottom: 2,
                alignItems: 'baseline',
              }}
            >
              <span style={{ ...labelStyle, minWidth: 80 }}>{key}</span>
              <span style={{ fontSize: TYPOGRAPHY.micro.size, color: NEUTRAL.text400, fontFamily: TYPOGRAPHY.mono.family }}>
                {typeof val === 'boolean' ? String(val) : typeof val === 'string' ? (val.length > 24 ? val.slice(0, 24) + '…' : val) : JSON.stringify(val)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
